import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Users, Plus, ArrowLeft, Edit, Trash2, Search,
  Phone, Mail, Percent, Check, X
} from 'lucide-react';
import { toast } from 'sonner';

export const AdminApporteurs = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [apporteurs, setApporteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    commission_rate: 50,
    password: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    fetchApporteurs();
  }, [isAdmin, authLoading, navigate]);

  const fetchApporteurs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/apporteurs`);
      setApporteurs(response.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const { password, email, ...updateData } = formData;
        await axios.put(`${API_URL}/api/admin/apporteurs/${editingId}`, updateData);
        toast.success('Apporteur modifié');
      } else {
        await axios.post(`${API_URL}/api/admin/apporteurs`, formData);
        toast.success('Apporteur créé');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchApporteurs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (apporteur) => {
    setFormData({
      name: apporteur.name,
      email: apporteur.email,
      phone: apporteur.phone || '',
      address: apporteur.address || '',
      commission_rate: apporteur.commission_rate,
      password: ''
    });
    setEditingId(apporteur.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet apporteur ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/apporteurs/${id}`);
      toast.success('Apporteur supprimé');
      fetchApporteurs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleActive = async (apporteur) => {
    try {
      await axios.put(`${API_URL}/api/admin/apporteurs/${apporteur.id}`, {
        is_active: !apporteur.is_active
      });
      toast.success(apporteur.is_active ? 'Apporteur désactivé' : 'Apporteur activé');
      fetchApporteurs();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      commission_rate: 50,
      password: ''
    });
  };

  const filteredApporteurs = apporteurs.filter(a => 
    a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion Apporteurs | Admin CAMBC</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-100 pt-20 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft size={24} />
              </Link>
              <h1 className="text-2xl font-bold text-slate-900">Gestion des Apporteurs</h1>
            </div>
            <Button onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }}>
              <Plus size={18} className="mr-2" /> Nouvel Apporteur
            </Button>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-lg">
                <CardHeader>
                  <CardTitle>{editingId ? 'Modifier Apporteur' : 'Nouvel Apporteur'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>Nom complet *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        disabled={!!editingId}
                      />
                    </div>
                    {!editingId && (
                      <div>
                        <Label>Mot de passe *</Label>
                        <Input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          required={!editingId}
                        />
                      </div>
                    )}
                    <div>
                      <Label>Téléphone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Adresse</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Taux de commission (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.commission_rate}
                        onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value)})}
                      />
                      <p className="text-xs text-slate-500 mt-1">Pourcentage de la commission courtier reversé à l'apporteur</p>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1">
                        {editingId ? 'Modifier' : 'Créer'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                        Annuler
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Apporteurs List */}
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-0">
              {filteredApporteurs.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {filteredApporteurs.map((apporteur) => (
                    <div key={apporteur.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          apporteur.is_active ? 'bg-blue-100' : 'bg-slate-200'
                        }`}>
                          <Users className={apporteur.is_active ? 'text-blue-600' : 'text-slate-400'} size={20} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{apporteur.name}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Mail size={14} /> {apporteur.email}
                            </span>
                            {apporteur.phone && (
                              <span className="flex items-center gap-1">
                                <Phone size={14} /> {apporteur.phone}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Percent size={14} /> {apporteur.commission_rate}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          apporteur.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {apporteur.is_active ? 'Actif' : 'Inactif'}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(apporteur)}>
                          {apporteur.is_active ? <X size={16} /> : <Check size={16} />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(apporteur)}>
                          <Edit size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(apporteur.id)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  <Users size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>Aucun apporteur trouvé</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
